/**
 * @file 分镜模块 React Query hooks
 */
import {useCallback} from 'react';
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query';
import message from '@/components/common/Message';
import {
    getStoryboardList,
    updateStoryboardCell,
    copyStoryboard,
    deleteStoryboard,
    deleteStoryboardMaterial,
    remarkMaterial,
    reorderStoryboard,
    createStoryboard,
} from '@/services/storyboardService';
import {MaterialGenStatusValue, PromptGenStatusValue} from '@/services/storyboardService/interface';
import type {
    StoryboardListData,
    Storyboard,
    UpdateStoryboardCellParams,
    DeleteStoryboardParams,
    RemarkMaterialParams,
    MaterialType,
    ReorderStoryboardData,
} from '@/services/storyboardService/interface';
import type {ProjectId} from '@/types';
import {sortMaterialsByAudit} from '@/pages/episode/utils';

export const storyboardKeys = {
    list: (projectId: ProjectId, episodeId?: number) =>
        ['storyboard', 'list', projectId, episodeId] as const,
};

/**
 * 是否需要对 list 响应进行合并
 * - 仅在轮询场景下（任一生成状态为 1）才合并，避免首次进入页面时缓存为空走 fallback。
 */
const shouldMerge = (data: StoryboardListData | undefined): boolean => {
    if (!data) return false;
    return (
        data.materialGenStatus === MaterialGenStatusValue.Generating ||
        data.promptGenStatus === PromptGenStatusValue.Generating
    );
};

/**
 * 将新 list 与缓存做按行合并：
 * - 行的增删以新 list 为准（拖拽/增删走 mutation 已 invalidate）
 * - 合并采用「fresh 为基底」：服务端权威字段（参考素材/分镜素材/音频/批注/审核状态等）
 *   一律取接口最新值，保证轮询期间这些服务端侧变更（如新批注 icon）能实时刷新；
 * - 仅回填少数「用户可编辑、可能含未保存编辑或在途乐观更新」的字段，避免覆盖本地状态：
 *   version/sortOrder（乐观更新）、duration/dialogue/characterId 及若干文本列。
 *   注意：imagePrompt/videoPrompt 属 AI 生成列，轮询期间需取 fresh，故不在回填之列。
 */
const mergeStoryboardList = (
    oldData: StoryboardListData | undefined,
    fresh: StoryboardListData,
): StoryboardListData => {
    if (!oldData?.storyboard || !fresh.storyboard) return fresh;
    const oldMap = new Map(oldData.storyboard.map((s) => [s.id, s]));
    const mergedRows: Storyboard[] = fresh.storyboard.map((freshRow) => {
        const oldRow = oldMap.get(freshRow.id);
        if (!oldRow) return freshRow;
        return {
            ...freshRow,
            version: oldRow.version,
            sortOrder: oldRow.sortOrder,
            duration: oldRow.duration,
            sceneType: oldRow.sceneType,
            sceneDesc: oldRow.sceneDesc,
            characterAction: oldRow.characterAction,
            lightAtmosphere: oldRow.lightAtmosphere,
            dialogue: oldRow.dialogue,
            sound: oldRow.sound,
            characterId: oldRow.characterId,
            characterName: oldRow.characterName,
        };
    });
    return {
        ...fresh,
        storyboard: mergedRows,
    };
};

/** 统一素材顺序为审核优先级：表格封面、预览弹窗、画布素材面板共用这份缓存，在数据入口排一次 */
const sortListMaterials = (data: StoryboardListData): StoryboardListData => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!data?.storyboard) return data;
    return {
        ...data,
        storyboard: data.storyboard.map((row) => ({
            ...row,
            imageMaterials: sortMaterialsByAudit(row.imageMaterials ?? []),
            videoMaterials: sortMaterialsByAudit(row.videoMaterials ?? []),
        })),
    };
};

/** 获取分镜列表 */
export const useStoryboardList = (
    projectId: ProjectId,
    episodeId?: number,
    options?: {enabled?: boolean; poll?: boolean},
) => {
    const {enabled = true, poll = true} = options ?? {};
    const queryClient = useQueryClient();
    const queryKey = storyboardKeys.list(projectId, episodeId);
    return useQuery<StoryboardListData>({
        queryKey,
        queryFn: async () => {
            const fresh = await getStoryboardList(projectId, episodeId);
            const cached = queryClient.getQueryData<StoryboardListData>(queryKey);
            // 仅在轮询场景下做合并；首次拉取或非轮询期直接以接口为准
            if (shouldMerge(cached)) {
                return sortListMaterials(mergeStoryboardList(cached, fresh));
            }
            return sortListMaterials(fresh);
        },
        staleTime: 0,
        enabled: enabled && !!projectId && !!episodeId,
        // 轮询：materialGenStatus / promptGenStatus 任一为 1 时每 3s 拉一次
        // poll=false 时（如画布页资源面板）完全不轮询
        refetchInterval: (query) => {
            if (!poll) return false;
            const data = query.state.data;
            if (!data) return false;
            const polling =
                data.materialGenStatus === MaterialGenStatusValue.Generating ||
                data.promptGenStatus === PromptGenStatusValue.Generating;
            return polling ? 3000 : false;
        },
        // 页面隐藏时不轮询，节省请求
        refetchIntervalInBackground: false,
    });
};

/** 修改分镜单元格内容 */
export const useUpdateStoryboardCell = (projectId: ProjectId, episodeId: number) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            storyboardId,
            data,
        }: {
            storyboardId: number;
            data: Omit<UpdateStoryboardCellParams, 'id' | 'projectId'>;
        }) => updateStoryboardCell({...data, id: storyboardId, projectId}),
        onMutate: async ({storyboardId, data}) => {
            await queryClient.cancelQueries({queryKey: storyboardKeys.list(projectId, episodeId)});
            // 乐观更新：立即将编辑值写入缓存，避免退出编辑态时闪烁
            queryClient.setQueryData(
                storyboardKeys.list(projectId, episodeId),
                (old: StoryboardListData | undefined) => {
                    if (!old?.storyboard) return old;
                    const {version: _version, episodeId: _epId, ...cellData} = data;
                    return {
                        ...old,
                        storyboard: old.storyboard.map((s) =>
                            s.id === storyboardId ? {...s, ...cellData} : s,
                        ),
                    };
                },
            );
        },
        onSuccess: (data) => {
            // 更新 version，避免连续编辑时乐观锁冲突
            queryClient.setQueryData(
                storyboardKeys.list(projectId, episodeId),
                (old: StoryboardListData | undefined) => {
                    if (!old?.storyboard) return old;
                    return {
                        ...old,
                        storyboard: old.storyboard.map((s) =>
                            s.id === data.storyboardId ? {...s, version: data.version} : s,
                        ),
                    };
                },
            );
            void queryClient.invalidateQueries({
                queryKey: storyboardKeys.list(projectId, episodeId),
            });
        },
        onError: () => {
            void queryClient.invalidateQueries({
                queryKey: storyboardKeys.list(projectId, episodeId),
            });
        },
    });
};

/** 复制分镜 */
export const useCopyStoryboard = (projectId: ProjectId, episodeId: number) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            sourceStoryboardId,
            prevSortOrder,
            nextSortOrder,
        }: {
            sourceStoryboardId: number;
            prevSortOrder: number;
            nextSortOrder: number;
        }) => copyStoryboard({sourceStoryboardId, prevSortOrder, nextSortOrder, projectId}),
        onSuccess: (data) => {
            // 先把新分镜插入到缓存中，按 sortOrder 排序，再触发列表接口失效
            if (!data) return;
            queryClient.setQueryData(
                storyboardKeys.list(projectId, episodeId),
                (old: StoryboardListData | undefined) => {
                    if (!old) return old;
                    const list = old.storyboard ?? [];
                    const next = [...list, data.storyboard].sort(
                        (a, b) => a.sortOrder - b.sortOrder,
                    );
                    return {...old, storyboard: next};
                },
            );
            void message.success('复制分镜成功');
            void queryClient.invalidateQueries({
                queryKey: storyboardKeys.list(projectId, episodeId),
            });
        },
        onError: () => {
            void queryClient.invalidateQueries({
                queryKey: storyboardKeys.list(projectId, episodeId),
            });
        },
    });
};

/** 删除分镜 */
export const useDeleteStoryboard = (projectId: ProjectId, episodeId: number) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: DeleteStoryboardParams) => deleteStoryboard(data),
        onSuccess: (_, variables) => {
            // 从缓存中移除该分镜
            queryClient.setQueryData(
                storyboardKeys.list(projectId, episodeId),
                (old: StoryboardListData | undefined) => {
                    if (!old?.storyboard) return old;
                    return {
                        ...old,
                        storyboard: old.storyboard.filter((s) => s.id !== variables.storyboardId),
                    };
                },
            );
            void message.success('删除分镜成功');
            void queryClient.invalidateQueries({
                queryKey: storyboardKeys.list(projectId, episodeId),
            });
        },
        onError: () => {
            void queryClient.invalidateQueries({
                queryKey: storyboardKeys.list(projectId, episodeId),
            });
        },
    });
};

interface DeleteStoryboardMaterialHookParams {
    storyboardId: number;
    materialId: number;
    materialType: MaterialType;
    version: number;
}

/**
 * 删除分镜素材（参考图/参考视频/音频/图片素材/视频素材）
 *
 * 采用乐观更新策略：请求发出时立即从缓存中移除对应素材，
 * 失败时通过 onMutate 返回的 previous 快照回滚，最后无论成败都触发列表失效以校准数据。
 */
export const useDeleteStoryboardMaterial = (projectId: ProjectId, episodeId?: number) => {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: ({
            storyboardId,
            materialId,
            materialType,
            version,
        }: DeleteStoryboardMaterialHookParams) =>
            deleteStoryboardMaterial({storyboardId, materialId, materialType, version, projectId}),
        onMutate: async ({storyboardId, materialId}) => {
            // 取消进行中的列表查询
            await queryClient.cancelQueries({queryKey: storyboardKeys.list(projectId, episodeId)});
            // 保存当前缓存快照，用于失败回滚
            const previous = queryClient.getQueryData<StoryboardListData>(
                storyboardKeys.list(projectId, episodeId),
            );

            // 乐观更新：从缓存中剔除待删除的素材，命中即置空
            queryClient.setQueryData(
                storyboardKeys.list(projectId, episodeId),
                (old: StoryboardListData | undefined) => {
                    if (!old?.storyboard) {
                        return old;
                    }

                    return {
                        ...old,
                        storyboard: old.storyboard.map((storyboard) => {
                            if (storyboard.id !== storyboardId) {
                                return storyboard;
                            }

                            // 对素材字段分别判断是否匹配待删除 materialId
                            return {
                                ...storyboard,
                                refImage:
                                    storyboard.refImage?.materialId === materialId
                                        ? null
                                        : storyboard.refImage,
                                refVideo:
                                    storyboard.refVideo?.materialId === materialId
                                        ? null
                                        : storyboard.refVideo,
                                audio:
                                    storyboard.audio?.materialId === materialId
                                        ? null
                                        : storyboard.audio,
                                imageMaterials: (storyboard.imageMaterials ?? []).filter(
                                    (material) => material.materialId !== materialId,
                                ),
                                videoMaterials: (storyboard.videoMaterials ?? []).filter(
                                    (material) => material.materialId !== materialId,
                                ),
                            };
                        }),
                    };
                },
            );

            // 返回的 context 会透传给 onError，用于回滚
            return {previous};
        },
        onSuccess: () => {
            void message.success('删除素材成功');
        },
        onError: (_error, _variables, context) => {
            // 回滚到请求前的快照
            if (context?.previous) {
                queryClient.setQueryData(
                    storyboardKeys.list(projectId, episodeId),
                    context.previous,
                );
            }
            void message.error('删除素材失败');
        },
        onSettled: () => {
            // 无论成功或失败，最终通过失效查询触发一次真实数据拉取，确保与后端一致
            void queryClient.invalidateQueries({
                queryKey: storyboardKeys.list(projectId, episodeId),
            });
        },
    });

    return {
        deleteStoryboardMaterialAsync: mutation.mutateAsync,
    };
};

/** 分镜素材批注（状态/备注） */
export const useRemarkMaterial = (projectId: ProjectId, episodeId: number) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (params: Omit<RemarkMaterialParams, 'projectId'>) =>
            remarkMaterial({...params, projectId}),
        onSuccess: (data) => {
            // 更新缓存中对应分镜的 version
            queryClient.setQueryData(
                storyboardKeys.list(projectId, episodeId),
                (old: StoryboardListData | undefined) => {
                    if (!old?.storyboard) return old;
                    return {
                        ...old,
                        storyboard: old.storyboard.map((s) =>
                            s.id === data.storyboardId ? {...s, version: data.version} : s,
                        ),
                    };
                },
            );
            void queryClient.invalidateQueries({
                queryKey: storyboardKeys.list(projectId, episodeId),
            });
        },
        onError: () => {
            void queryClient.invalidateQueries({
                queryKey: storyboardKeys.list(projectId, episodeId),
            });
        },
    });
};

/**
 * 分镜拖拽重排序
 *
 * 乐观更新：拖拽结束后立即交换缓存中分镜的 sortOrder，再异步调用后端持久化。
 * 成功时用后端返回的 sortOrder / version 更新缓存；失败时回滚到拖拽前的快照。
 */
export const useSortStoryboard = (projectId: ProjectId, episodeId: number) => {
    const queryClient = useQueryClient();

    const mutation = useMutation({
        mutationFn: ({
            storyboardId,
            prevSortOrder,
            nextSortOrder,
        }: {
            storyboardId: number;
            prevSortOrder: number;
            nextSortOrder: number;
            activeId: number;
            overId: number;
        }) => reorderStoryboard({storyboardId, prevSortOrder, nextSortOrder, projectId}),
        onMutate: async ({activeId, overId}) => {
            const listKey = storyboardKeys.list(projectId, episodeId);
            await queryClient.cancelQueries({queryKey: listKey});
            const previous = queryClient.getQueryData<StoryboardListData>(listKey);

            // 乐观更新：仅调整被拖项的 sortOrder 至目标位置邻居之间，保留其他项原有 sortOrder，
            // 避免与后端返回的真实排序值不自洽导致再次跳动
            if (previous?.storyboard) {
                const list = [...previous.storyboard].sort((a, b) => a.sortOrder - b.sortOrder);
                const activeIndex = list.findIndex((s) => s.id === activeId);
                const overIndex = list.findIndex((s) => s.id === overId);
                if (activeIndex !== -1 && overIndex !== -1) {
                    const moved = [...list];
                    const [item] = moved.splice(activeIndex, 1);
                    moved.splice(overIndex, 0, item);
                    const prev = overIndex > 0 ? moved[overIndex - 1] : null;
                    const next = overIndex < moved.length - 1 ? moved[overIndex + 1] : null;
                    // 估算一个介于前后邻居之间的 sortOrder，保证排序顺序与目标一致
                    const optimisticSortOrder: number =
                        prev && next
                            ? (prev.sortOrder + next.sortOrder) / 2
                            : prev
                              ? prev.sortOrder + 1
                              : next
                                ? next.sortOrder - 1
                                : item.sortOrder;
                    queryClient.setQueryData(listKey, {
                        ...previous,
                        storyboard: previous.storyboard.map((s) =>
                            s.id === activeId ? {...s, sortOrder: optimisticSortOrder} : s,
                        ),
                    });
                }
            }

            return {previous};
        },
        onSuccess: (data: ReorderStoryboardData) => {
            // 用后端返回的真实 sortOrder 和 version 更新被拖项；其他项 sortOrder 保持不变
            queryClient.setQueryData(
                storyboardKeys.list(projectId, episodeId),
                (old: StoryboardListData | undefined) => {
                    if (!old?.storyboard) return old;
                    return {
                        ...old,
                        storyboard: old.storyboard.map((s) =>
                            s.id === data.storyboardId
                                ? {...s, sortOrder: data.sortOrder, version: data.version}
                                : s,
                        ),
                    };
                },
            );
        },
        onError: (_error, _variables, context) => {
            if (context?.previous) {
                queryClient.setQueryData(
                    storyboardKeys.list(projectId, episodeId),
                    context.previous,
                );
            }
            void message.error('排序失败');
            // 失败时再 refetch 拉取真实数据校准
            void queryClient.invalidateQueries({
                queryKey: storyboardKeys.list(projectId, episodeId),
            });
        },
    });

    /**
     * 执行排序：将 activeId 移动到 overId 的位置
     */
    const sortStoryboard = useCallback(
        (activeId: number, overId: number) => {
            const listKey = storyboardKeys.list(projectId, episodeId);
            const old = queryClient.getQueryData<StoryboardListData>(listKey);
            if (!old?.storyboard) return;

            const list = [...old.storyboard].sort((a, b) => a.sortOrder - b.sortOrder);
            const activeIndex = list.findIndex((s) => s.id === activeId);
            const overIndex = list.findIndex((s) => s.id === overId);
            if (activeIndex === -1 || overIndex === -1) return;

            // 计算目标位置的 prevSortOrder / nextSortOrder
            const moved = [...list];
            const [item] = moved.splice(activeIndex, 1);
            moved.splice(overIndex, 0, item);
            const prev = overIndex > 0 ? moved[overIndex - 1] : null;
            const next = overIndex < moved.length - 1 ? moved[overIndex + 1] : null;
            const prevSortOrder = prev ? prev.sortOrder : 0;
            const nextSortOrder = next ? next.sortOrder : prev ? prev.sortOrder + 1 : 1;

            mutation.mutate({
                storyboardId: activeId,
                prevSortOrder,
                nextSortOrder,
                activeId,
                overId,
            });
        },
        [projectId, episodeId, queryClient, mutation],
    );

    return {sortStoryboard};
};

/** 新增空分镜（指定位置） */
export const useCreateStoryboard = (projectId: ProjectId, episodeId: number) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({
            prevSortOrder,
            nextSortOrder,
        }: {
            prevSortOrder: number;
            nextSortOrder: number;
        }) => createStoryboard({prevSortOrder, nextSortOrder, projectId, episodeId}),
        onSuccess: () => {
            void message.success('新增分镜成功');
            void queryClient.invalidateQueries({
                queryKey: storyboardKeys.list(projectId, episodeId),
            });
        },
        onError: () => {
            void message.error('新增分镜失败');
            void queryClient.invalidateQueries({
                queryKey: storyboardKeys.list(projectId, episodeId),
            });
        },
    });
};
